import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { HeadphonesIcon, Plus, Loader2, Send, MessageSquare, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { AttachmentPreview, AttachmentUploader } from '../../components/support/SupportAttachment';

const uploadSupportFile = (formData) => api.post('/messages/upload-attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });

  const getApiPrefix = () => {
    if (user?.role === 'property_manager') return '/manager';
    if (user?.role === 'tenant') return '/tenant';
    return '/landlord';
  };

  useEffect(() => {
    loadTickets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    if (ticket.unread_for_owner) {
      try {
        await api.post(`/support-tickets/${ticket.ticket_id}/mark-read`);
        setTickets((prev) => prev.map((t) => (t.ticket_id === ticket.ticket_id ? { ...t, unread_for_owner: false } : t)));
        window.dispatchEvent(new Event('badge-refresh'));
      } catch (err) {
        // Non-critical: marking a ticket read shouldn't block opening it
        console.error('Failed to mark support ticket as read:', err);
      }
    }
  };

  const loadTickets = async () => {
    try {
      const response = await api.get(`${getApiPrefix()}/support-tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      await api.post(`${getApiPrefix()}/support-tickets`, newTicket);
      toast.success('Support ticket created successfully');
      setCreateDialogOpen(false);
      setNewTicket({ subject: '', message: '', priority: 'normal' });
      loadTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if ((!replyText.trim() && !replyAttachment) || !selectedTicket) return;

    setSending(true);
    try {
      await api.post(`${getApiPrefix()}/support-tickets/${selectedTicket.ticket_id}/reply`, {
        message: replyText,
        ...(replyAttachment || {}),
      });
      toast.success('Reply sent');
      setReplyText('');
      setReplyAttachment(null);
      loadTickets();
      // Refresh selected ticket
      const updated = await api.get(`${getApiPrefix()}/support-tickets/${selectedTicket.ticket_id}`);
      setSelectedTicket(updated.data);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50"><Clock className="w-3 h-3 mr-1" />Open</Badge>;
      case 'in_progress':
        return <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50"><AlertCircle className="w-3 h-3 mr-1" />In Progress</Badge>;
      case 'resolved':
        return <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50"><CheckCircle className="w-3 h-3 mr-1" />Resolved</Badge>;
      case 'closed':
        return <Badge variant="secondary">Closed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge variant="destructive">High</Badge>;
      case 'normal':
        return <Badge variant="secondary">Normal</Badge>;
      case 'low':
        return <Badge variant="outline">Low</Badge>;
      default:
        return <Badge>{priority}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Support</h1>
          <p className="text-muted-foreground mt-1">Get help and submit support tickets</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="new-ticket-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <HeadphonesIcon className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Support Tickets</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Need help? Create a support ticket and our team will assist you.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Ticket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map((ticket) => (
            <Card 
              key={ticket.ticket_id} 
              className="cursor-pointer hover:border-primary/50 transition-colors"
              data-testid={`ticket-${ticket.ticket_id}`}
              onClick={() => handleOpenTicket(ticket)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate flex items-center gap-2">
                      {ticket.unread_for_owner && (
                        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" data-testid={`ticket-unread-${ticket.ticket_id}`} title="New reply" />
                      )}
                      {ticket.created_by_admin && (
                        <Badge variant="default" className="text-[10px] shrink-0">From Support</Badge>
                      )}
                      <span className="truncate">{ticket.subject}</span>
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      Created {formatDateTime(ticket.created_at)}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {getPriorityBadge(ticket.priority)}
                    {getStatusBadge(ticket.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2">{ticket.message}</p>
                {ticket.replies?.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <MessageSquare className="w-3 h-3" />
                    {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>
              Describe your issue and our support team will get back to you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                placeholder="Brief description of your issue"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={newTicket.priority}
                onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                placeholder="Describe your issue in detail..."
                rows={5}
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} disabled={sending}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <DialogTitle>{selectedTicket?.subject}</DialogTitle>
                <DialogDescription>
                  Ticket #{selectedTicket?.ticket_id?.slice(-8)} • Created {selectedTicket && formatDateTime(selectedTicket.created_at)}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                {selectedTicket && getPriorityBadge(selectedTicket.priority)}
                {selectedTicket && getStatusBadge(selectedTicket.status)}
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Original Message */}
            <div className={`rounded-lg p-4 ${selectedTicket?.created_by_admin ? 'bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-500' : 'bg-muted/50'}`}>
              <div className="text-xs font-medium mb-2">{selectedTicket?.created_by_admin ? 'Support Team' : 'You'}</div>
              <p className="text-sm whitespace-pre-wrap">{selectedTicket?.message}</p>
              {selectedTicket && (
                <AttachmentPreview url={selectedTicket.attachment_url} name={selectedTicket.attachment_name} type={selectedTicket.attachment_type} testid="support-original-attachment" />
              )}
            </div>
            
            {/* Replies */}
            {selectedTicket?.replies?.map((reply, index) => (
              <div
                key={reply.reply_id || `${reply.created_at}-${index}`}
                className={`rounded-lg p-4 ${
                  reply.user_role === 'admin' 
                    ? 'bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-500' 
                    : 'bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium">
                    {reply.user_role === 'admin' ? 'Support Team' : 'You'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(reply.created_at)}
                  </span>
                </div>
                {reply.message && <p className="text-sm whitespace-pre-wrap">{reply.message}</p>}
                <AttachmentPreview url={reply.attachment_url} name={reply.attachment_name} type={reply.attachment_type} />
              </div>
            ))}
          </div>
          
          {/* Reply Input */}
          {selectedTicket?.status !== 'closed' && (
            <div className="border-t pt-4 space-y-2">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={2}
                  className="flex-1"
                  data-testid="reply-input"
                />
                <Button onClick={handleReply} disabled={sending || (!replyText.trim() && !replyAttachment)} data-testid="send-reply-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <AttachmentUploader
                attachment={replyAttachment}
                onChange={setReplyAttachment}
                uploadFn={uploadSupportFile}
                testidPrefix="support-reply"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
